import { DateFormat } from '@equip.health/ui';
import { isoToTimezoneDateTime } from '../../utils';
import { getQualifyingWeekCount } from './buckets.util';

export const FOLLOW_UP_SERIES_MIN_COVERAGE_RATIO = 0.6;

export const FOLLOW_UP_SERIES_MIN_WEEKS_FLOOR = 2;

interface SeriesDraft {
  key: string;
  weekdayLabel: string;
  timeLabel: string;
  chipLabel: string;
  openingsByWeek: Map<number, FollowUpOpening>;
}

export const deriveFollowUpSeries = (
  buckets: FollowUpWeekBucket[],
  timezone?: string,
  minCoverageRatio: number = FOLLOW_UP_SERIES_MIN_COVERAGE_RATIO,
): FollowUpSeries[] => {
  const byKey = new Map<string, SeriesDraft>();

  for (const bucket of buckets) {
    if (bucket.weekIndex === 0) continue;
    for (const opening of bucket.openings) {
      let draft = byKey.get(opening.seriesKey);
      if (!draft) {
        const start = isoToTimezoneDateTime(opening.startTime, timezone);
        draft = {
          key: opening.seriesKey,
          weekdayLabel: `${start.toFormat(DateFormat.fullDay)}s`,
          timeLabel: opening.timeLabel,
          chipLabel: `${start.toFormat(DateFormat.fullDay)}, ${start.toFormat('h:mm')} ${start
            .toFormat('a')
            .toLowerCase()}`,
          openingsByWeek: new Map(),
        };
        byKey.set(opening.seriesKey, draft);
      }
      if (!draft.openingsByWeek.has(bucket.weekIndex)) {
        draft.openingsByWeek.set(bucket.weekIndex, opening);
      }
    }
  }

  const qualifyingWeekCount = getQualifyingWeekCount(buckets);
  if (qualifyingWeekCount === 0) return [];

  return Array.from(byKey.values())
    .filter((d) => {
      const fullWeekCoverage = d.openingsByWeek.size;
      return (
        fullWeekCoverage >= FOLLOW_UP_SERIES_MIN_WEEKS_FLOOR &&
        fullWeekCoverage / qualifyingWeekCount >= minCoverageRatio
      );
    })
    .map((d) => ({
      key: d.key,
      weekdayLabel: d.weekdayLabel,
      timeLabel: d.timeLabel,
      label: `${d.weekdayLabel} \u00B7 ${d.timeLabel}`,
      chipLabel: d.chipLabel,
      openingsByWeek: d.openingsByWeek,
      weekCoverage: d.openingsByWeek.size,
    }))
    .sort((a, b) => {
      if (b.weekCoverage !== a.weekCoverage) {
        return b.weekCoverage - a.weekCoverage;
      }
      const [aDay, aTime] = a.key.split('|');
      const [bDay, bTime] = b.key.split('|');
      return Number(aDay) - Number(bDay) || aTime.localeCompare(bTime);
    });
};
